import { LoadingButton, LoadingButtonProps } from "@mui/lab";
import { Grid, Stack, SxProps, Tooltip } from "@mui/material";
import Button, { ButtonProps } from "@mui/material/Button";
import DeleteIcon from "assets/icons/DeleteIcon";
import EditIcon from "assets/icons/EditIcon";
import ButtonWithPermissions, {
  ButtonWithPermissionsProps,
  generateUnavailableText,
} from "components/buttons/ButtonWithPermissions";
import { CompanyContext } from "context/companyContext";
import useIsCreate from "hooks/useIsCreate";
import { Dispatch, SetStateAction, useContext } from "react";

type SaveEditButtonsProps = {
  isEdited: boolean;
  isEditable?: boolean;
  editingButtonDisable?: boolean;
  editingButtonTooltipText?: string;
  onSubmit: () => void;
  setIsEdited: Dispatch<SetStateAction<boolean>>;
  loading?: boolean;
  deleteText?: string;
  topAdditionalContent?: JSX.Element;
  bottomAdditionalContent?: JSX.Element;
  onDelete?: () => void;
  horizontalMode?: boolean;
  wrapperSx?: SxProps;
  cancelButtonProps?: ButtonProps;
  deleteButtonProps?: ButtonProps;
  createButtonProps?: Partial<ButtonWithPermissionsProps>;
  saveButtonProps?: LoadingButtonProps;
};

const SaveEditButtons = ({
  isEdited,
  isEditable = true,
  editingButtonDisable = false,
  editingButtonTooltipText,
  onSubmit,
  setIsEdited,
  loading,
  deleteText,
  topAdditionalContent,
  bottomAdditionalContent,
  onDelete,
  horizontalMode,
  wrapperSx,
  cancelButtonProps,
  deleteButtonProps,
  createButtonProps,
  saveButtonProps,
}: SaveEditButtonsProps) => {
  const isCreate = useIsCreate();
  const { isCreateAvailable, currentCompany } = useContext(CompanyContext);

  const saveButton = isCreate ? (
    <ButtonWithPermissions
      buttonText='Сохранить'
      onClick={onSubmit}
      tooltipText={generateUnavailableText(isCreateAvailable, currentCompany, "создания")}
      loading={loading}
      type='submit'
      {...createButtonProps}
    />
  ) : (
    isEditable && (
      <LoadingButton
        loading={loading}
        variant='contained'
        onClick={onSubmit}
        fullWidth
        size='medium'
        type='submit'
        {...saveButtonProps}>
        Сохранить
      </LoadingButton>
    )
  );

  const cancelButton = (
    <Button
      size='medium'
      variant='outlined'
      color='secondary'
      fullWidth
      onClick={() => setIsEdited(false)}
      {...cancelButtonProps}>
      Отменить
    </Button>
  );

  const deleteButton = (
    <Button
      size='medium'
      sx={{
        textAlign: "left",
        "&:hover .MuiSvgIcon-root": {
          color: "primary.main",
        },
      }}
      endIcon={<DeleteIcon sx={{ width: 24, height: 24, color: "base.200" }} />}
      variant='outlined'
      color='secondary'
      fullWidth
      onClick={onDelete}
      {...deleteButtonProps}>
      {deleteText}
    </Button>
  );

  if (!isEdited) {
    return (
      <Tooltip title={editingButtonTooltipText}>
        {/* span для корректной работы Tooltip */}
        <span>
          <LoadingButton
            endIcon={<EditIcon />}
            onClick={() => setIsEdited(true)}
            variant='contained'
            disabled={editingButtonDisable}
            loading={loading}
            sx={{
              py: 1,
              px: 2,
              width: "max-content",
              "& .MuiButton-endIcon": { margin: 0 },
            }}
          />
        </span>
      </Tooltip>
    );
  }

  return horizontalMode ? (
    <Grid
      container
      spacing={1}
      sx={wrapperSx}>
      {topAdditionalContent && !isCreate && (
        <Grid
          item
          xs={12}
          md={6}>
          {topAdditionalContent}
        </Grid>
      )}
      {bottomAdditionalContent && !isCreate && (
        <Grid
          item
          xs={12}
          md={6}>
          {bottomAdditionalContent}
        </Grid>
      )}
      {onDelete && !isCreate && (
        <Grid
          item
          xs={12}
          md={6}>
          {deleteButton}
        </Grid>
      )}
      <Grid
        item
        xs={12}
        md={6}>
        {saveButton}
      </Grid>
      {!isCreate && (
        <Grid
          item
          xs={12}
          md={6}>
          {cancelButton}
        </Grid>
      )}
    </Grid>
  ) : (
    <Stack sx={wrapperSx}>
      {saveButton}
      {!isCreate && (
        <Stack
          spacing={4}
          sx={{ mt: 1 }}>
          <Stack spacing={1}>
            {cancelButton}
            {topAdditionalContent}
          </Stack>
          {(onDelete || bottomAdditionalContent) && (
            <Stack spacing={1}>
              {bottomAdditionalContent}
              {onDelete && deleteButton}
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default SaveEditButtons;
